import { readClaims } from './claims.js';

/**
 * API client for the portals (plan 03 §5.1). Sends the access token, turns error
 * bodies into `ApiError` with the stable code the APIs return, and on
 * `401 STEP_UP_REQUIRED` asks the user to sign in again and retries once.
 *
 * ADR 242 (plan 242 §6):
 * - a plain 401 (a role change, a removal) forces one token refresh and retries once;
 *   a second 401 calls `onUnauthorized` (the portal shows the picker or signs out);
 * - with `expectedTenant`, a token for another clinic than the one this page loaded
 *   with is never sent (another tab switched clinic; the tabs share tokens): the
 *   request fails with `CLINIC_SWITCHED` and `onTenantMismatch` is called.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** The whole error body, e.g. per-field compliance reasons on a 422. */
    readonly body?: unknown,
  ) {
    super(`${status} ${code}`);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  token: (forceRefresh?: boolean) => Promise<string | undefined>;
  /** Re-sign-in for a sensitive write; resolves true when the user did it. */
  stepUp?: () => Promise<boolean>;
  /** ADR 242: the clinic this page loaded with; undefined = no guard (staff, personal). */
  expectedTenant?: () => string | undefined;
  /** ADR 242: the token is for another clinic (switched in another tab). */
  onTenantMismatch?: () => void;
  /** ADR 242: still 401 after a refresh (removed, or no clinic left). */
  onUnauthorized?: () => void;
  /**
   * Plan 257 EF-08 follow-up: API Gateway refused the route itself (a bare 403 with no
   * error code: the authorizer allows this token `GET /me` only, e.g. the MFA hold),
   * still after a token refresh. The session reads the token again and decides.
   */
  onAccessHeld?: () => void;
  fetch?: typeof fetch;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown, headers?: Record<string, string>): Promise<T>;
  patch<T>(path: string, body: unknown): Promise<T>;
  put<T>(path: string, body: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
}

/** The token's clinic, or undefined for a token that is not a readable JWT. */
function tenantOf(token: string): string | undefined {
  try {
    return readClaims(token).tenantId;
  } catch {
    return undefined;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetch ?? fetch.bind(globalThis);

  async function send<T>(
    method: string,
    path: string,
    body?: unknown,
    headers: Record<string, string> = {},
    retried = false,
    refreshed = false,
  ): Promise<T> {
    const token = await options.token(retried || refreshed);
    if (!token) throw new ApiError(401, 'UNAUTHORIZED');
    const expected = options.expectedTenant?.();
    if (expected !== undefined && tenantOf(token) !== expected) {
      options.onTenantMismatch?.();
      throw new ApiError(409, 'CLINIC_SWITCHED');
    }
    const res = await doFetch(`${options.baseUrl}${path}`, {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined && { 'content-type': 'application/json' }),
        ...headers,
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });
    const text = await res.text();
    const data: unknown = text ? JSON.parse(text) : undefined;
    if (res.ok) return data as T;

    const code =
      (data as { error?: { code?: string } } | undefined)?.error?.code ?? `HTTP_${res.status}`;
    if (res.status === 401 && code === 'STEP_UP_REQUIRED' && !retried && options.stepUp) {
      if (await options.stepUp()) return send<T>(method, path, body, headers, true, refreshed);
    }
    if (res.status === 401 && code !== 'STEP_UP_REQUIRED') {
      if (!refreshed) return send<T>(method, path, body, headers, retried, true);
      options.onUnauthorized?.();
    }
    // The authorizer's own refusal has no `error.code` (our APIs always send one).
    if (res.status === 403 && code === 'HTTP_403') {
      if (!refreshed) return send<T>(method, path, body, headers, retried, true);
      options.onAccessHeld?.();
      throw new ApiError(403, 'ACCESS_HELD', data);
    }
    throw new ApiError(res.status, code, data);
  }

  return {
    get: (path) => send('GET', path),
    post: (path, body, headers) => send('POST', path, body, headers),
    patch: (path, body) => send('PATCH', path, body),
    put: (path, body) => send('PUT', path, body),
    delete: (path) => send('DELETE', path),
  };
}
